/**
 * @file
 * Appointment date/time picker – input dropdown with vanilla-calendar-pro.
 *
 * A readonly text input displays the selected appointment. Clicking the
 * input opens a dropdown panel containing a vanilla-calendar-pro calendar
 * and time-slot buttons. Selecting a time slot updates the hidden form
 * inputs, updates the display text, and closes the dropdown.
 */

import { Calendar, type FormatDateString } from 'vanilla-calendar-pro';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface TpAppointmentConfig {
  availableDates: string[];
  timeSlotsMap: Record<string, string[]>;
}

/** Groups DOM references needed by `renderTimeSlots`. */
interface TimeSlotsContext {
  grid: HTMLElement;
  emptyMsg: HTMLElement;
  liveRegion: HTMLElement;
  timeInput: HTMLInputElement;
  displayInput: HTMLInputElement;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/**
 * Returns the Drupal site language code (e.g. "fr", "en") from the `<html>`
 * element. Falls back to the browser default if not set.
 */
function getSiteLocale(): string {
  return document.documentElement.lang || navigator.language || 'en';
}

/**
 * Formats a date string for display (e.g. "19 Mar 2026, 09:30").
 *
 * The display format uses `Drupal.t()` so translators can rearrange
 * the order of day, month, year, and time for different locales.
 */
function formatDisplay(date: string, time: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  const locale = getSiteLocale();
  const day = String(d.getDate());
  const month = d.toLocaleString(locale, { month: 'short' });
  const year = String(d.getFullYear());
  return Drupal.t('@day @month @year, @time', {
    '@day': day,
    '@month': month,
    '@year': year,
    '@time': time,
  });
}

/**
 * Formats a date string for screen reader announcement (e.g. "19 March 2026").
 */
function formatDateForAnnouncement(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  const locale = getSiteLocale();
  const day = String(d.getDate());
  const month = d.toLocaleString(locale, { month: 'long' });
  const year = String(d.getFullYear());
  return Drupal.t('@day @month @year', {
    '@day': day,
    '@month': month,
    '@year': year,
  });
}

/* ── Roving tabindex for time-slot grid ──────────────────────────────────── */

/**
 * Manages roving tabindex within a grid of time-slot buttons.
 *
 * @description
 * Only one button in the grid has tabindex="0" (the active one);
 * all others have tabindex="-1". Arrow keys move focus between buttons.
 */
function initRovingTabindex(grid: HTMLElement): void {
  grid.addEventListener('keydown', (e: KeyboardEvent) => {
    const buttons = Array.from(grid.querySelectorAll<HTMLButtonElement>('.tp-appointment-datetime__time-btn'));
    if (buttons.length === 0) return;

    const current = document.activeElement as HTMLButtonElement;
    const currentIndex = buttons.indexOf(current);
    if (currentIndex === -1) return;

    let nextIndex = -1;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    if (nextIndex >= 0 && nextIndex !== currentIndex) {
      current.setAttribute('tabindex', '-1');
      buttons[nextIndex].setAttribute('tabindex', '0');
      buttons[nextIndex].focus();
    }
  });
}

/**
 * Sets roving tabindex on time-slot buttons after rendering.
 *
 * The selected (aria-pressed="true") button or the first button
 * receives tabindex="0"; all others receive tabindex="-1".
 */
function applyRovingTabindex(grid: HTMLElement): void {
  const buttons = Array.from(grid.querySelectorAll<HTMLButtonElement>('.tp-appointment-datetime__time-btn'));
  if (buttons.length === 0) return;

  const selected = buttons.find((b) => b.getAttribute('aria-pressed') === 'true');
  const active = selected || buttons[0];

  for (const btn of buttons) {
    btn.setAttribute('tabindex', btn === active ? '0' : '-1');
  }
}

/* ── Time-slot rendering ─────────────────────────────────────────────────── */

/**
 * Renders time-slot buttons for the given date.
 *
 * @description
 * When a button is clicked the hidden inputs are updated, the display
 * input text is set, and the provided `onSelect` callback fires
 * (used to close the dropdown). Uses roving tabindex so only one
 * button is in the tab order at a time.
 */
function renderTimeSlots(
  date: string,
  timeSlotsMap: Record<string, string[]>,
  ctx: TimeSlotsContext,
  onSelect: () => void,
  selectedTime = ''
): void {
  const { grid, emptyMsg, liveRegion, timeInput, displayInput } = ctx;
  const slots = timeSlotsMap[date] || [];
  const restoredTime = selectedTime && slots.includes(selectedTime) ? selectedTime : '';

  grid.replaceChildren();
  timeInput.value = restoredTime;
  if (!restoredTime) {
    timeInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const formattedDate = formatDateForAnnouncement(date);

  if (slots.length === 0) {
    grid.setAttribute('hidden', '');
    emptyMsg.removeAttribute('hidden');
    liveRegion.textContent = Drupal.t('No time slots available for @date.', {
      '@date': formattedDate,
    });
    return;
  }

  emptyMsg.setAttribute('hidden', '');
  grid.removeAttribute('hidden');

  // Build all buttons first so the cached list can be used in click handlers.
  const buttons: HTMLButtonElement[] = [];

  for (const slot of slots) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tp-appointment-datetime__time-btn';
    btn.textContent = slot;
    btn.setAttribute('aria-pressed', slot === restoredTime ? 'true' : 'false');
    btn.setAttribute('data-time', slot);

    if (slot === restoredTime) {
      displayInput.value = formatDisplay(date, slot);
    }

    btn.addEventListener('click', () => {
      // Deselect all siblings using cached list.
      for (const b of buttons) {
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('tabindex', '-1');
      }

      btn.setAttribute('aria-pressed', 'true');
      btn.setAttribute('tabindex', '0');
      timeInput.value = slot;
      timeInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Update display input.
      displayInput.value = formatDisplay(date, slot);

      // Announce selection to screen readers.
      Drupal.announce(
        Drupal.t('Selected @time on @date.', {
          '@time': slot,
          '@date': formattedDate,
        })
      );

      // Close the dropdown.
      onSelect();
    });

    buttons.push(btn);
    grid.appendChild(btn);
  }

  // Consolidate roving tabindex logic in one place.
  applyRovingTabindex(grid);

  liveRegion.textContent = Drupal.t('@count time slots available for @date.', {
    '@count': String(slots.length),
    '@date': formattedDate,
  });

  // Move focus to the first time-slot button so keyboard users
  // can immediately start selecting.
  if (buttons[0]) {
    buttons[0].focus();
  }
}

/* ── Drupal behaviour ────────────────────────────────────────────────────── */

((Drupal, once) => {
  'use strict';

  /** Tracks cleanup functions keyed by element ID for detach. */
  const cleanupMap = new Map<string, () => void>();

  /**
   * Initialises the appointment date/time dropdown picker.
   *
   * @description
   * Mounts a vanilla-calendar-pro instance inside the dropdown panel and
   * populates time-slot buttons when a date is selected. Handles open/close
   * of the dropdown via the trigger input, outside clicks, and Escape key.
   * Implements keyboard navigation following WCAG 2.2 patterns for dialogs
   * and roving tabindex for the time-slot grid.
   */
  Drupal.behaviors.tpAppointmentDatetime = {
    attach(context: Document | HTMLElement, settings: DrupalSettings) {
      const wrappers = once('tp-appointment-datetime', '[data-tp-appointment-datetime]', context);

      wrappers.forEach((wrapper) => {
        const elementId = wrapper.getAttribute('data-tp-appointment-datetime');
        if (!elementId) return;

        const config: TpAppointmentConfig = ((settings.tpAppointmentDatetime as Record<string, TpAppointmentConfig>) ||
          {})[elementId] || { availableDates: [], timeSlotsMap: {} };

        const timeSlotsMap = config.timeSlotsMap || {};
        const enableDates = (config.availableDates || []) as FormatDateString[];

        // DOM references.
        const displayInput = wrapper.querySelector<HTMLInputElement>('[data-tp-appointment-display]');
        const toggleBtn = wrapper.querySelector<HTMLButtonElement>('[data-tp-appointment-toggle]');
        const dropdown = wrapper.querySelector<HTMLElement>('[data-tp-appointment-dropdown]');
        const calendarEl = wrapper.querySelector<HTMLElement>('[data-tp-appointment-calendar]');
        const dateInput = wrapper.querySelector<HTMLInputElement>('[data-tp-appointment-date-input]');
        const timeInput = wrapper.querySelector<HTMLInputElement>('[data-tp-appointment-time-input]');
        const grid = wrapper.querySelector<HTMLElement>('[data-tp-appointment-timeslots-grid]');
        const emptyMsg = wrapper.querySelector<HTMLElement>('[data-tp-appointment-timeslots-empty]');
        const liveRegion = wrapper.querySelector<HTMLElement>('[data-tp-appointment-live]');

        if (
          !displayInput ||
          !toggleBtn ||
          !dropdown ||
          !calendarEl ||
          !dateInput ||
          !timeInput ||
          !grid ||
          !emptyMsg ||
          !liveRegion
        ) {
          return;
        }

        // Shared context object passed to renderTimeSlots.
        const ctx: TimeSlotsContext = {
          grid,
          emptyMsg,
          liveRegion,
          timeInput,
          displayInput,
        };

        // Wire up aria-controls so both triggers reference the dropdown panel.
        const dropdownId = `${elementId}-dropdown`;
        dropdown.id = dropdownId;
        displayInput.setAttribute('aria-controls', dropdownId);
        toggleBtn.setAttribute('aria-controls', dropdownId);

        // Initialise roving tabindex on the time-slot grid.
        initRovingTabindex(grid);

        // ── Dropdown open / close ────────────────────────────────────────

        let isOpen = false;

        const setExpanded = (expanded: boolean) => {
          const value = expanded ? 'true' : 'false';
          displayInput.setAttribute('aria-expanded', value);
          toggleBtn.setAttribute('aria-expanded', value);
        };

        const handleOutsideClick = (e: MouseEvent) => {
          if (!wrapper.contains(e.target as Node)) {
            closeDropdown();
          }
        };

        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closeDropdown();
          }
        };

        const openDropdown = () => {
          if (isOpen) return;
          isOpen = true;
          dropdown.removeAttribute('hidden');
          setExpanded(true);
          wrapper.classList.add('tp-appointment-datetime--open');
          document.addEventListener('click', handleOutsideClick, true);
          document.addEventListener('keydown', handleEscape);

          // Move focus into the dropdown for keyboard and screen reader users.
          // Try the selected date button first, then the first enabled date,
          // then fall back to the first focusable element in the dropdown.
          requestAnimationFrame(() => {
            const selectedDate = dropdown.querySelector<HTMLButtonElement>(
              '.vc-date[data-vc-date-selected] .vc-date__btn'
            );
            const firstEnabledDate = dropdown.querySelector<HTMLButtonElement>(
              '.vc-date:not([data-vc-date-disabled]) .vc-date__btn'
            );
            const firstFocusable = dropdown.querySelector<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            const target = selectedDate || firstEnabledDate || firstFocusable;
            if (target) {
              target.focus();
            }
          });
        };

        const closeDropdown = () => {
          if (!isOpen) return;
          isOpen = false;
          dropdown.setAttribute('hidden', '');
          setExpanded(false);
          wrapper.classList.remove('tp-appointment-datetime--open');
          document.removeEventListener('click', handleOutsideClick, true);
          document.removeEventListener('keydown', handleEscape);
          displayInput.focus();
        };

        const toggleDropdown = () => {
          isOpen ? closeDropdown() : openDropdown();
        };

        // Keyboard activation on display input (Enter/Space toggle).
        const handleDisplayKeydown = (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          }
        };

        displayInput.addEventListener('click', toggleDropdown);
        displayInput.addEventListener('keydown', handleDisplayKeydown);
        toggleBtn.addEventListener('click', toggleDropdown);

        // ── Calendar initialisation ──────────────────────────────────────
        const sorted = enableDates.slice().sort();
        let initialYear: number;
        let initialMonth: number;

        if (sorted.length > 0) {
          const [sy, sm] = sorted[0].split('-').map(Number);
          initialYear = sy;
          initialMonth = sm - 1;
        } else {
          const now = new Date();
          initialYear = now.getFullYear();
          initialMonth = now.getMonth();
        }

        const minDate = sorted.length > 0 ? sorted[0] : ('today' as FormatDateString);
        const maxDate = sorted.length > 0 ? sorted[sorted.length - 1] : ('today' as FormatDateString);

        const calendar = new Calendar(calendarEl, {
          type: 'default',
          firstWeekday: 1,
          selectionDatesMode: 'single',
          disableAllDates: true,
          enableDates,
          locale: getSiteLocale(),
          selectedTheme: 'light',
          selectedYear: initialYear,
          selectedMonth: initialMonth as Parameters<typeof Calendar.prototype.set>[0]['selectedMonth'],
          dateMin: minDate,
          dateMax: maxDate,
          onClickDate(self) {
            const selected = self.context.selectedDates;
            if (!selected || selected.length === 0) return;

            const dateStr = selected[0];
            dateInput.value = dateStr;
            dateInput.dispatchEvent(new Event('change', { bubbles: true }));

            renderTimeSlots(dateStr, timeSlotsMap, ctx, closeDropdown);
          },
        });

        calendar.init();

        // Restore display if a value was already selected.
        if (dateInput.value && timeInput.value) {
          displayInput.value = formatDisplay(dateInput.value, timeInput.value);
        }
        if (dateInput.value && timeSlotsMap[dateInput.value]) {
          renderTimeSlots(dateInput.value, timeSlotsMap, ctx, () => {}, timeInput.value);
        }

        // ── Cleanup registration for detach ──────────────────────────────

        cleanupMap.set(elementId, () => {
          // Close dropdown and remove document-level listeners.
          if (isOpen) {
            isOpen = false;
            dropdown.setAttribute('hidden', '');
            setExpanded(false);
            wrapper.classList.remove('tp-appointment-datetime--open');
            document.removeEventListener('click', handleOutsideClick, true);
            document.removeEventListener('keydown', handleEscape);
          }

          // Remove element-level listeners.
          displayInput.removeEventListener('click', toggleDropdown);
          displayInput.removeEventListener('keydown', handleDisplayKeydown);
          toggleBtn.removeEventListener('click', toggleDropdown);

          // Destroy the calendar instance if possible.
          if (typeof calendar.destroy === 'function') {
            calendar.destroy();
          }
        });
      });
    },

    /**
     * Cleans up event listeners and calendar instances on AJAX removal.
     */
    detach(context: Document | HTMLElement, _settings: DrupalSettings, trigger: string) {
      if (trigger !== 'unload') return;

      const elements = once.remove(
        'tp-appointment-datetime',
        '[data-tp-appointment-datetime]',
        context
      ) as HTMLElement[];

      elements.forEach((wrapper) => {
        const elementId = wrapper.getAttribute('data-tp-appointment-datetime');
        if (elementId && cleanupMap.has(elementId)) {
          cleanupMap.get(elementId)!();
          cleanupMap.delete(elementId);
        }
      });
    },
  };
})(Drupal, once);
